import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { differenceInYears } from 'date-fns';
import { useFormikContext } from 'formik';
import { Chip } from '@material-ui/core';
import { seniorAge, inexperiencedYearsLimit } from '@ourbranch/lookups';

import { Button } from 'core/components/button';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import MACarAssignmentModal from './ma-car-assignment-modal';
import { useStyles } from '../../car-assignment.styles';

const MACarAssignment = ({ disabled }) => {
  const classes = useStyles();
  const [showAssignCarsModal, setShowAssignCarsModal] = useState(false);

  const { values } = useFormikContext();
  const { drivers, cars } = values || {};

  const { subtitle, driversToDisplay, isSenior } = useMemo(() => {
    const calculateAge = (driver) => {
      let age = driver.age;
      if (!age) {
        const today = new Date();
        const birthDate = new Date(driver.dateOfBirth);
        age = differenceInYears(today, birthDate);
      }
      return age;
    };
    const seniorDrivers = drivers.filter((d) => calculateAge(d) >= seniorAge.MA);
    const inexperiencedDrivers = drivers.filter(
      (d) => calculateAge(d) - d.ageFirstLicensed < inexperiencedYearsLimit.MA
    );

    if (seniorDrivers.length === 1 && inexperiencedDrivers.length === 0) {
      return {
        subtitle: 'Please answer the following information regarding the senior driver',
        driversToDisplay: seniorDrivers,
        isSenior: true
      };
    }
    return {
      subtitle:
        'Please answer these questions related to main vehicle for all drivers with less than 6 years driving experience. Note that a vehicle cannot have more than one main driver.',
      driversToDisplay: inexperiencedDrivers
    };
  }, [drivers, cars]);

  return (
    driversToDisplay.length > 0 && (
      <>
        <Section
          title="Car Assignments"
          type="SubSection"
          right={
            !disabled && (
              <Button color="secondary" onClick={() => setShowAssignCarsModal(true)}>
                Edit Car Assignments
              </Button>
            )
          }
        >
          {driversToDisplay.map((driver) => {
            const assignedCars = cars.filter(
              (c) => driver.userAssignedCar === c.VIN || c.userAssignedDriver === driver.id
            );
            return (
              <Card className={classes.card} type="primary" key={driver.id}>
                <Label className={classes.left}>
                  {driver.firstName} {driver.lastName}
                </Label>
                <div className={classes.rightContainer}>
                  <Label className={classes.assignation}>
                    {assignedCars.length > 0 ? 'Main driver of' : 'Is not the main driver of any vehicle'}
                  </Label>
                  {assignedCars.map((car) => (
                    <Chip
                      label={`${car.year} ${car.make} ${car.model}`}
                      classes={{ root: classes.chipRoot }}
                      key={car.VIN}
                    />
                  ))}
                </div>
              </Card>
            );
          })}
        </Section>
        {!disabled && showAssignCarsModal && (
          <MACarAssignmentModal
            open={showAssignCarsModal}
            onClose={() => setShowAssignCarsModal(false)}
            drivers={driversToDisplay}
            subtitle={subtitle}
            isSenior={isSenior}
          />
        )}
      </>
    )
  );
};

MACarAssignment.propTypes = {
  disabled: PropTypes.bool.isRequired
};

export default MACarAssignment;
